/* Admin Center — Titles (Billing Roles) tab (extracted from AdminPage.tsx) */

import { useState, useEffect } from 'react';
import { ArrowLeft, Check, GripVertical, Trash2 } from 'lucide-react';
import { ColorPickerPopover } from '../../components/ui/color-picker';
import { Icon } from '../../components/ui/Icon';
import EmptyState from '../../components/EmptyState';
import { TITLE_COLORS } from '../../lib/palette';
import { rem } from '../../lib/rem';
import { AdminService } from '../../services/adminService';
import type { BillingRole } from '../../../shared/types';
import { useTableSync } from '../../hooks/useTableSync';
import type { AdminPageProps } from './types';
import {
  inputStyle,
  btnPrimary,
  btnDanger,
  DRAG_HANDLE_STYLE,
  useDragReorder,
  RowSkeleton,
} from './shared';

/* ─── Titles Tab (was "Billing Roles" pre-v2.9.0) ───
   The table is still called `billing_roles` in the schema — the rename
   is a UI relabel only.  Migration 015 absorbed company_roles into here
   as display_only=true rows with no default rate. */

function BillingRolesTab({ toast }: { toast: AdminPageProps['toast'] }) {
  const [billingRoles, setBillingRoles] = useState<BillingRole[]>([]);
  const [editing, setEditing] = useState<BillingRole | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
  }, []);
  useTableSync(['billing_roles'], () => load());
  async function load() {
    try {
      const list = await AdminService.billingRoles.list();
      setBillingRoles(list.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)));
    } finally {
      setLoading(false);
    }
  }

  function empty(): BillingRole {
    return {
      id: '',
      name: '',
      abbreviation: '',
      color: TITLE_COLORS[billingRoles.length % TITLE_COLORS.length],
      defaultRate: undefined,
      displayOnly: false,
      sortOrder: billingRoles.length,
      createdAt: '',
      updatedAt: '',
    };
  }

  async function save() {
    if (!editing) return;
    if (!editing.name.trim()) {
      toast('Enter a title name', true);
      return;
    }
    try {
      await AdminService.billingRoles.save(editing);
      await load();
      setEditing(null);
      toast('Title saved');
    } catch (err: any) {
      toast('Save failed: ' + err.message, true);
    }
  }

  async function remove(id: string) {
    if (
      !confirm('Delete this title? Any team members assigned to it will have their title cleared.')
    )
      return;
    try {
      await AdminService.billingRoles.delete(id);
      if (editing?.id === id) setEditing(null);
      await load();
      toast('Title deleted');
    } catch (err: any) {
      toast('Delete failed: ' + err.message, true);
    }
  }

  // Hook MUST be called before the early return below — rules-of-hooks.
  const drag = useDragReorder(billingRoles, async (reordered) => {
    setBillingRoles(reordered);
    try {
      await Promise.all(
        reordered.map((it, i) => {
          if ((billingRoles.find((x) => x.id === it.id)?.sortOrder ?? -1) !== i) {
            return AdminService.billingRoles.save({ ...it, sortOrder: i });
          }
          return Promise.resolve();
        })
      );
    } catch (err: any) {
      toast('Reorder failed: ' + err.message, true);
      await load();
    }
  });

  if (editing) {
    return (
      <div style={{ maxWidth: rem(580) }}>
        <button
          onClick={() => setEditing(null)}
          style={{
            ...btnDanger,
            display: 'inline-flex',
            alignItems: 'center',
            gap: rem(6),
            marginBottom: 'var(--space-3)',
            color: 'var(--tm)',
            border: '1px solid var(--bd)',
          }}
        >
          <Icon icon={ArrowLeft} em={1.0833} /> Back
        </button>
        <h3 style={{ fontSize: rem(15), fontWeight: 700, marginBottom: 'var(--space-4)' }}>
          {editing.id ? 'Edit Title' : 'New Title'}
        </h3>

        <div style={{ display: 'flex', gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
          <label style={{ flex: 1 }}>
            <div
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                color: 'var(--tm)',
                marginBottom: 'var(--space-1)',
              }}
            >
              Title
            </div>
            <input
              value={editing.name}
              onChange={(e) =>
                setEditing((prev) => (prev ? { ...prev, name: e.target.value } : prev))
              }
              style={inputStyle}
              placeholder="e.g. Senior Engineer, Project Manager, BIM Manager"
            />
          </label>
          <label style={{ width: rem(120) }}>
            <div
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                color: 'var(--tm)',
                marginBottom: 'var(--space-1)',
              }}
            >
              Abbreviation
            </div>
            <input
              value={editing.abbreviation || ''}
              onChange={(e) =>
                setEditing((prev) => (prev ? { ...prev, abbreviation: e.target.value } : prev))
              }
              style={{ ...inputStyle, textTransform: 'uppercase' }}
              maxLength={6}
              placeholder="SE"
            />
          </label>
        </div>

        <div style={{ marginBottom: 'var(--space-4)' }}>
          <div
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              color: 'var(--tm)',
              marginBottom: rem(6),
            }}
          >
            Color
          </div>
          <ColorPickerPopover
            value={editing.color || TITLE_COLORS[0]}
            onChange={(hex) => setEditing((prev) => (prev ? { ...prev, color: hex } : prev))}
            swatches={TITLE_COLORS}
            aria-label="Title color"
          />
        </div>

        {/* Display-only toggle — when checked, Title is HR/directory only
            and is excluded from invoice rate-card dropdowns by default. */}
        <div
          style={{
            marginBottom: 'var(--space-4)',
            padding: `${rem(10)} var(--space-3)`,
            background: 'var(--sa)',
            borderRadius: 'var(--radius-md)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: rem(10),
            cursor: 'pointer',
          }}
          onClick={() =>
            setEditing((prev) => (prev ? { ...prev, displayOnly: !prev.displayOnly } : prev))
          }
          role="checkbox"
          aria-checked={editing.displayOnly}
          aria-label="Display-only title (no rate)"
        >
          <div
            style={{
              width: rem(18),
              height: rem(18),
              borderRadius: 'var(--radius-sm)',
              flexShrink: 0,
              border: `2px solid ${editing.displayOnly ? 'var(--ac)' : 'var(--bd)'}`,
              background: editing.displayOnly ? 'var(--ac)' : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--color-text-inverse)',
              fontSize: 'var(--font-size-sm)',
              marginTop: rem(1),
            }}
          >
            {editing.displayOnly ? <Icon icon={Check} size="md" strokeWidth={3} /> : ''}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--tx)' }}>
              Display-only title (no rate)
            </div>
            <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)', marginTop: rem(2) }}>
              Use for HR/directory titles like "BIM Manager" or "Admin" that aren't normally billed
              to clients. The invoice generator hides display-only titles from rate-card dropdowns.
            </div>
          </div>
        </div>

        {!editing.displayOnly && (
          <label style={{ display: 'block', marginBottom: 'var(--space-4)' }}>
            <div
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                color: 'var(--tm)',
                marginBottom: 'var(--space-1)',
              }}
            >
              Default Rate ($/hr)
            </div>
            <input
              type="number"
              min="0"
              step="5"
              value={editing.defaultRate ?? ''}
              onChange={(e) =>
                setEditing((prev) =>
                  prev
                    ? {
                        ...prev,
                        defaultRate: e.target.value ? parseFloat(e.target.value) : undefined,
                      }
                    : prev
                )
              }
              style={{ ...inputStyle, width: rem(180), fontFamily: "'JetBrains Mono', monospace" }}
              placeholder="0"
            />
            <div
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--tm)',
                marginTop: 'var(--space-1)',
              }}
            >
              Default rate inherited by team members assigned to this Title. Per-person overrides
              are still possible. This value is gated — only users with{' '}
              <code style={{ fontSize: 'var(--font-size-xs)' }}>team_members.view_details</code> can
              see it outside Admin Center.
            </div>
          </label>
        )}

        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <button onClick={save} style={btnPrimary}>
            Save Title
          </button>
          {editing.id && (
            <button onClick={() => remove(editing.id)} style={btnDanger}>
              Delete
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 'var(--space-4)',
        }}
      >
        <div style={{ fontSize: 'var(--font-size-base)', color: 'var(--tm)' }}>
          Titles are how the app identifies what people do — "Senior Engineer", "Project Manager",
          "BIM Manager", "Admin". Each Title can carry a default billing rate (gated to users with
          rate-view permission). Display-only Titles are HR/directory only and don't appear in
          invoice rate-card pickers. Drag the handle to reorder.
        </div>
        <button onClick={() => setEditing(empty())} style={btnPrimary}>
          + Add Title
        </button>
      </div>
      {loading ? (
        <RowSkeleton label="Loading titles" />
      ) : (
        <div
          style={{
            background: 'var(--sf)',
            borderRadius: rem(8),
            border: '1px solid var(--bd)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '40px 32px 110px 1fr 140px 60px',
              gap: 'var(--space-3)',
              padding: 'var(--space-2) var(--space-3)',
              background: 'var(--sa)',
              borderBottom: '1px solid var(--bd)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 700,
              color: 'var(--tm)',
              textTransform: 'uppercase',
              letterSpacing: '.04em',
            }}
          >
            <span></span>
            <span></span>
            <span>Abbr.</span>
            <span>Title</span>
            <span>Default Rate</span>
            <span></span>
          </div>
          {billingRoles.map((br, i) => {
            const isDragging = drag.dragIdx === i;
            const isDropTarget = drag.overIdx === i && drag.dragIdx !== null && drag.dragIdx !== i;
            return (
              <div
                key={br.id}
                {...drag.rowProps(i)}
                onClick={() => setEditing({ ...br })}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '40px 32px 110px 1fr 140px 60px',
                  gap: 'var(--space-3)',
                  alignItems: 'center',
                  padding: 'var(--space-2) var(--space-3)',
                  borderBottom: i < billingRoles.length - 1 ? '1px solid var(--bd)' : 'none',
                  background: isDragging
                    ? 'rgba(var(--acr),0.08)'
                    : isDropTarget
                      ? 'rgba(var(--acr),0.04)'
                      : undefined,
                  opacity: isDragging ? 0.5 : 1,
                  cursor: 'pointer',
                  transition: 'background .1s',
                }}
              >
                <span
                  {...drag.handleProps()}
                  onClick={(e) => e.stopPropagation()}
                  style={{ ...DRAG_HANDLE_STYLE, ...drag.handleProps().style }}
                  title="Drag to reorder"
                  aria-label="Drag to reorder"
                >
                  <Icon icon={GripVertical} em={14 / 16} />
                </span>
                <span
                  style={{
                    width: rem(20),
                    height: rem(20),
                    borderRadius: rem(5),
                    background: br.color || 'var(--bd)',
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                    fontWeight: 700,
                    color: 'var(--tx)',
                    fontFamily: "'JetBrains Mono', monospace",
                    letterSpacing: '.02em',
                  }}
                >
                  {br.abbreviation || '\u2014'}
                </span>
                <span style={{ fontWeight: 600, fontSize: 'var(--font-size-base)' }}>
                  {br.name}
                  {br.displayOnly && (
                    <span
                      style={{
                        marginLeft: 'var(--space-2)',
                        fontSize: 'calc(var(--font-size-base) * 9 / 13)',
                        fontWeight: 700,
                        padding: `${rem(2)} ${rem(6)}`,
                        borderRadius: 'var(--radius-sm)',
                        background: 'var(--sa)',
                        color: 'var(--tm)',
                        textTransform: 'uppercase',
                        letterSpacing: '.05em',
                      }}
                    >
                      Display only
                    </span>
                  )}
                </span>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 'var(--font-size-base)',
                    color: 'var(--tx)',
                  }}
                >
                  {br.displayOnly
                    ? '\u2014'
                    : br.defaultRate != null
                      ? `$${br.defaultRate}/hr`
                      : '\u2013'}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    remove(br.id);
                  }}
                  style={btnDanger}
                  title="Delete title"
                  aria-label={`Delete title ${br.name}`}
                >
                  <Icon icon={Trash2} em={14 / 12} />
                </button>
              </div>
            );
          })}
          {billingRoles.length === 0 && (
            <EmptyState
              title="No titles yet"
              subtitle='Add what people are called: "Senior Engineer", "Project Manager", "BIM Manager", "Admin", etc.'
            />
          )}
        </div>
      )}
    </div>
  );
}

export { BillingRolesTab };
